/**
 * high-pass.ts describes a high-pass filter meeting the provided specifications.
 * It provides all the methods promised by `FilterSignature`.
 */

import FilterConstants from '../../filter-constants';
import FrequencyRange from '../process-data/frequency';
import { Complex, createComplex } from '../process-data/complex';
import { createTimeAxis } from '../formulas/time-axis';
import FilterResponse from '../filter-response/filter-response';
import { SpecificationParameters, FilterSignature, FilterStage, StepResponse } from './filter';

export default {
  get name(): string {
    return FilterConstants.filterType.highPass;
  },

  getStages(specificationParameters: SpecificationParameters): FilterStage[] {
    const filterResponse = FilterResponse.create({ selectedResponseType: specificationParameters.filterResponseType });
    const stages = filterResponse.lowPass(
      specificationParameters.passbandAttenuation,
      specificationParameters.stopbandAttenuation,
      1,
      _getFStop(specificationParameters.passbandFrequency, specificationParameters.stopbandFrequency),
      specificationParameters.filterResponseValue,
    );
    const stageLetterArray = FilterConstants.stageLetter;

    return stages.map((item: FilterStage, index: number) => {
      item.stageType = FilterConstants.filterType.highPass;
      item.stageLetter = stageLetterArray[index] ?? '';
      item.stageFp = specificationParameters.passbandFrequency / item.stageFp;
      item.stageFz = 0;

      return item;
    });
  },

  getOrder(specificationParameters: SpecificationParameters): number {
    const filterResponse = FilterResponse.create({ selectedResponseType: specificationParameters.filterResponseType });

    return filterResponse.getOrder(
      specificationParameters.passbandAttenuation,
      specificationParameters.stopbandAttenuation,
      1,
      _getFStop(specificationParameters.passbandFrequency, specificationParameters.stopbandFrequency),
      specificationParameters.filterResponseValue,
    );
  },

  getNumberOfStages(specificationParameters: SpecificationParameters): number {
    const filterResponse = FilterResponse.create({ selectedResponseType: specificationParameters.filterResponseType });

    return filterResponse.getNumberOfStages(
      specificationParameters.passbandAttenuation,
      specificationParameters.stopbandAttenuation,
      1,
      _getFStop(specificationParameters.passbandFrequency, specificationParameters.stopbandFrequency),
      specificationParameters.filterResponseValue,
    );
  },

  getFilterFrequencyRange(stopbandFrequency: number, passbandFrequency: number): FrequencyRange {
    const startFrequency = stopbandFrequency / 10;
    const stopFrequency = passbandFrequency * 1000;
    const decade = Math.log10(stopFrequency) - Math.log10(startFrequency);

    const result = FrequencyRange.create({
      startFrequency: startFrequency,
      stopFrequency: stopFrequency,
      pointsPerDecade: 100,
      decade: decade,
    });

    return result;
  },

  getActualStageSpecFrequency(loopFp: number): FrequencyRange {
    const startFrequency = loopFp / 5;
    const stopFrequency = loopFp * 2;

    const result = FrequencyRange.create({
      startFrequency: startFrequency,
      stopFrequency: stopFrequency,
      pointsPerDecade: 50,
    });

    return result;
  },

  createFirstOrderFilterAmplitude(fp: number, gain: number, frequencyArray: number[]): Complex[] {
    return frequencyArray.map((frequency) => {
      const complexGain = createComplex(gain, 0);
      const complexNum = createComplex(0, (complexGain.real * frequency) / fp);
      const res = complexNum.divide(createComplex(1, frequency / fp));
      return res;
    });
  },

  createSecondOrderFilterAmplitude(fp: number, Q: number, gain: number, frequencyArray: number[]): Complex[] {
    return frequencyArray.map((frequency) => {
      const complexGain = createComplex(gain, 0);
      const complexDenom = createComplex(
        (fp * fp - frequency * frequency) / (-1 * frequency * frequency),
        (frequency * fp) / Q / (-1 * frequency * frequency),
      );
      const res = complexGain.divide(complexDenom);

      return res;
    });
  },

  createTimeAxis(passbandFrequency: number): number[] {
    return createTimeAxis((passbandFrequency * 10) / 10);
  },

  setupFirstOrderStepResponse(stageFp: number, gain: number): StepResponse {
    return { w0: stageFp * 2 * Math.PI, Q: 0, c: 0, a: 0, b: -1 * gain };
  },

  setupSecondOrderStepResponse(stageFp: number, stageQ: number, gain: number): StepResponse {
    return { w0: stageFp * 2 * Math.PI, Q: stageQ, a: gain, b: 0, c: 0 };
  },

  getImpulseArrayFirstElement(gain: number): number {
    return gain;
  },

  getSecondOrderStageImplementation(): string {
    // TODO: relax restriction and make the inductor implementation for high pass work properly one day when and if it comes to it
    // if (
    //   stageFp * gain * (0.5 + stageQ) > FilterConstants.Limits.INDUCTOR_PREFERRED_FREQ_X_GAIN_X_Q &&
    //   stageFp > FilterConstants.Limits.INDUCTOR_ALLOWED_FREQ
    // ) {
    //   result = FilterConstants.implementationType.inductor;
    // }

    return FilterConstants.implementationType.sallenKey;
  },

  //getMaxFrequency used in voltage range calculations.  We don't look at magnitudes beyond this frequency for voltage range because we don't care if they get clipped.
  getMaxFrequency(_stopbandFrequency: number, frequencyArray: number[]): number {
    return Math.max(...frequencyArray);
  },

  getAdcFrequency(stopFrequency: number): number {
    return stopFrequency / 10;
  },
} as FilterSignature;

function _getFStop(passbandFrequency: number, stopbandFrequency: number): number {
  return passbandFrequency / stopbandFrequency;
}
